import { getCoordinateDistance } from './geometry';

type FilterPropsType = {
    entities: any,
    filters: any
};

/** Ids of the company profile (or of a manual selection) that requirements are compared against. */
export type RequirementIdsType = {
    vehicleTypeIds: number[],
    equipmentIds: number[],
    licenceCertificateIds: number[],
    sustainabilityIds: number[]
};

/**
 * Same rule as the API (watchlist matching): no requirement -> ok; vehicle types must overlap;
 * equipment and certificates must be a subset of the profile; an empty profile category is not filtered.
 * Sustainability is strict: when `sustainabilityIds` is given (even empty) ALL of the entity's sustainability
 * requirements must be in it. Leave it out (undefined) to not filter on sustainability, e.g. for an
 * empty manual selection. The profile's ids include Fair Transport when the company is a member.
 */
export function meetsRequirements(entity: any, profile: Partial<RequirementIdsType>) {
    const vehicleTypeIds: number[] = entity.vehicleTypeIds ?? [];
    const equipmentIds: number[] = entity.equipmentIds ?? [];
    const licenceCertificateIds: number[] = entity.licenceCertificateIds ?? [];
    const profileVehicleTypeIds = profile.vehicleTypeIds ?? [];
    const profileEquipmentIds = profile.equipmentIds ?? [];
    const profileLicenceCertificateIds = profile.licenceCertificateIds ?? [];
    const sustainabilityIds: number[] = entity.sustainabilityIds ?? [];

    return (
        vehicleTypeIds.length === 0
        || profileVehicleTypeIds.length === 0
        || vehicleTypeIds.some((id) => profileVehicleTypeIds.includes(id))
    ) && (
        profileEquipmentIds.length === 0
        || equipmentIds.every((id) => profileEquipmentIds.includes(id))
    ) && (
        profileLicenceCertificateIds.length === 0
        || licenceCertificateIds.every((id) => profileLicenceCertificateIds.includes(id))
    ) && (
        !profile.sustainabilityIds
        || sustainabilityIds.every((id) => profile.sustainabilityIds?.includes(id))
    );
}

/** True when the market filter has requirement selections made by hand (not the fulfil-profile default). */
export function hasManualRequirements(filters: any): boolean {
    return [filters?.vehicleTypeIds, filters?.equipmentIds, filters?.licenceCertificateIds, filters?.sustainabilityIds]
        .some((ids) => Array.isArray(ids) && ids.length > 0);
}

/**
 * Filters that make a watchlist's own hits: geography/publisher come from the API's matchedWatchlistIds.
 * takesOffer means "without external assignments". Expired are always hidden, as when opened in the market.
 */
export function watchlistToMarketFilters(watchlist: any) {
    return {
        ...watchlist,
        watchlistId: watchlist.id,
        routeId: null,
        publisherTenantId: null,
        isDhl: !watchlist.takesOffer,
        hideExpired: true
    };
}

/**
 * Filters that make a group's own hits: what opening the group in the market shows (see market filter defaults).
 * `fulfilsProfile` is the market's default for carriers (getDefaultFulfilsProfile), null until it is known.
 */
export function groupToMarketFilters(groupId: number, fulfilsProfile: RequirementIdsType | null = null) {
    return {
        isJob: true,
        isCapacity: true,
        groupId,
        hideExpired: true,
        fulfilsProfile
    };
}

/** Choosing a place or a route replaces the other area. */
export function getAreaReplacement(area: 'place' | 'route') {
    return area === 'place'
        ? { routeId: null, routeName: '', insideRoutes: false }
        : { placeName: '', placeLat: null, placeLng: null };
}

/** A watchlist's area as editable filter values. A polygon has no filter field, it stays with the selected watchlist. */
export function getWatchlistAreaFilters(w: any) {
    if (w.routeId) {
        return { routeId: w.routeId, routeName: w.routeName ?? '', routeMatchMode: w.matchMode };
    }
    if (w.area?.type === 'circle') {
        return {
            placeName: w.name || 'Bevakningens område',
            placeLat: w.area.shape.center.lat,
            placeLng: w.area.shape.center.lng,
            placeRadius: w.area.shape.radius,
            placeMatchMode: w.matchMode
        };
    }
    return { watchlistHasPolygon: w.area?.type === 'polygon' };
}

const WATCHLIST_AREA_KEYS = [
    'routeId', 'routeMatchMode', 'insideRoutes', 'placeLat', 'placeLng', 'placeRadius', 'placeMatchMode'
];
const WATCHLIST_VALUE_KEYS = [
    'isJob', 'isCapacity', 'isDhl', 'groupId', 'hasListPrice', 'publisherTenantId',
    'minGrossWeight', 'maxGrossWeight', 'minChargeableWeight', 'maxChargeableWeight', 'minVolume', 'maxVolume',
    'minLoadingMeters', 'maxLoadingMeters', 'minPalletPlaces', 'maxPalletPlaces', 'minPallets', 'maxPallets'
];

/**
 * The watchlist filter is the exact hits of the saved watchlist, so changing one of its values lets go of it and the
 * filled in values decide. A polygon is only kept by the watchlist, which therefore stays until the area is changed.
 * The watchlist is remembered as the source, for updating it with the changed values.
 */
export function getWatchlistDetachment(current: any, changes: any) {
    if (!current.watchlistId || 'watchlistId' in changes) return {};
    const changed = (key: string) => key in changes && changes[key] !== current[key];
    const source = { sourceWatchlistId: current.watchlistId, sourceWatchlistName: current.watchlistName };
    if (WATCHLIST_AREA_KEYS.some(changed)
        || (!current.watchlistHasPolygon && WATCHLIST_VALUE_KEYS.some(changed))) {
        return {
            ...source, watchlistId: null, watchlistName: '', watchlistHasPolygon: false
        };
    }
    return WATCHLIST_VALUE_KEYS.some(changed) ? source : {};
}

/** The area "Spara som bevakning" saves: the filter's one area, none means whole country. */
export function getWatchlistGeography(f: any, watchlists: any[]): any {
    if (f.routeId) {
        return { routeId: f.routeId, matchMode: f.routeMatchMode === 'within' ? 'within' : 'touches' };
    }
    if (f.placeLat !== null && f.placeLat !== undefined) {
        return {
            area: { type: 'circle', shape: { center: { lat: f.placeLat, lng: f.placeLng }, radius: f.placeRadius } },
            matchMode: f.placeMatchMode
        };
    }
    const w = f.watchlistId ? watchlists.find((x) => String(x.id) === String(f.watchlistId)) : null;
    return w && (w.routeId || w.area) ? { routeId: w.routeId, area: w.area, matchMode: w.matchMode } : {};
}

/** The area when updating a watchlist: a polygon still selected is kept, no area in the filter means whole country. */
export function getWatchlistUpdateGeography(f: any, watchlists: any[]): any {
    if (f.watchlistId && f.watchlistHasPolygon) return {};
    return { routeId: null, area: null, ...getWatchlistGeography(f, watchlists) };
}

/**
 * Route filter. routeMatchMode 'touches' (default): pickup OR delivery in the route; 'within': both.
 * "Inside our routes" (insideRoutes) is the same rule over all the company's routes.
 */
function matchesRoute(e: any, f: any) {
    const pickup: number[] = e.pickupRouteIds ?? [];
    const delivery: number[] = e.deliveryRouteIds ?? [];
    const within = f.routeMatchMode === 'within';
    if (f.routeId) {
        const p = pickup.includes(f.routeId);
        const d = delivery.includes(f.routeId);
        if (!(within ? p && d : p || d)) return false;
    }
    if (f.insideRoutes) {
        const p = pickup.length > 0;
        const d = delivery.length > 0;
        if (!(within ? p && d : p || d)) return false;
    }
    return true;
}

/** Place with radius, on the entity's coordinates. Same modes as matchesRoute: 'touches' (default) or 'within'. */
function matchesPlace(e: any, f: any) {
    if (f.placeLat === null || f.placeLat === undefined || !f.placeRadius) return true;
    const center = { lat: f.placeLat, lng: f.placeLng };
    const p = getCoordinateDistance(center, { lat: e.pickupLat, lng: e.pickupLng }) <= f.placeRadius;
    const d = getCoordinateDistance(center, { lat: e.deliveryLat, lng: e.deliveryLng }) <= f.placeRadius;
    return f.placeMatchMode === 'within' ? p && d : p || d;
}

export function filterMarketEntities({ entities, filters: f }: FilterPropsType) {
    return typeof f !== 'object' ? entities
        : entities.filter((e: any) => (
            e.type === 'CAP' || (f.isJob && (e.type !== 'DHL' || f.isDhl !== false))
        ) && (
            f.isCapacity || (e.type !== 'CAP')
        ) && (
            !f.watchlistId || (e.matchedWatchlistIds ?? []).includes(Number(f.watchlistId))
        ) && (
            // A selected watchlist is its own area, the filled in values only show it
            !!f.watchlistId || matchesRoute(e, f)
        ) && (
            !!f.watchlistId || matchesPlace(e, f)
        ) && (
            !f.publisherTenantId || e.publisherTenantId === f.publisherTenantId
        ) && (
            meetsRequirements(e, {
                vehicleTypeIds: f.vehicleTypeIds ?? [],
                equipmentIds: f.equipmentIds ?? [],
                licenceCertificateIds: f.licenceCertificateIds ?? [],
                sustainabilityIds: f.sustainabilityIds?.length ? f.sustainabilityIds : undefined
            })
        ) && (
            !f.fulfilsProfile || meetsRequirements(e, f.fulfilsProfile)
        ) && (
            !f.firstPickupDate || (e.firstPickupDate >= f.firstPickupDate)
        ) && (
            !f.lastDeliveryDate || (e.lastDeliveryDate <= f.lastDeliveryDate)
        ) && (
            !f.minGrossWeight || (e.grossWeight >= f.minGrossWeight)
        ) && (
            !f.maxGrossWeight || (e.grossWeight <= f.maxGrossWeight)
        ) && (
            !f.minChargeableWeight || (e.chargeableWeight >= f.minChargeableWeight)
        ) && (
            !f.maxChargeableWeight || (e.chargeableWeight <= f.maxChargeableWeight)
        ) && (
            !f.minVolume || (e.volume >= f.minVolume)
        ) && (
            !f.maxVolume || (e.volume <= f.maxVolume)
        ) && (
            !f.minLoadingMeters || (e.loadingMeters >= f.minLoadingMeters)
        ) && (
            !f.maxLoadingMeters || (e.loadingMeters <= f.maxLoadingMeters)
        ) && (
            !f.minPalletPlaces || (e.palletPlaces >= f.minPalletPlaces)
        ) && (
            !f.maxPalletPlaces || (e.palletPlaces <= f.maxPalletPlaces)
        ) && (
            !f.minPallets || (e.pallets >= f.minPallets)
        ) && (
            !f.maxPallets || (e.pallets <= f.maxPallets)
        // ) && (
        //     !f.minPieces || (e.pieces >= f.minPieces)
        // ) && (
        //     !f.maxPieces || (e.pieces <= f.maxPieces)
        ) && (
            !f.groupId || (e.groupIds.some((gid: number) => gid === f.groupId))
        ) && (
            !f.hasListPrice || e.type === 'CAP' || e.listPrice !== null
        ) && (
            !f.hasOffer || e.type === 'CAP' || !!e.offer
        ) && (
            !f.hideExpired || e.type !== 'LL' || !e.expiresAt || e.expiresAt > (new Date()).toISOString()
        ));
}
